import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, Building2, Landmark, Receipt, HardHat, Wallet,
  TrendingUp, ListChecks, Repeat, Wand2, ArrowLeftRight, LogOut, ExternalLink, FileSignature,
  Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/backoffice-finance", label: "Vue Groupe", icon: LayoutDashboard, end: true },
  { to: "/backoffice-finance/tresorerie", label: "Trésorerie", icon: Wallet },
  { to: "/backoffice-finance/entites", label: "Entités", icon: Building2 },
  { to: "/backoffice-finance/comptes", label: "Comptes bancaires", icon: Landmark },
  { to: "/backoffice-finance/transactions", label: "Transactions", icon: ArrowLeftRight },
  { to: "/backoffice-finance/baux", label: "Baux locatifs", icon: FileSignature },
  { to: "/backoffice-finance/projets", label: "Projets & Travaux", icon: HardHat },
  { to: "/backoffice-finance/prets", label: "Prêts bancaires", icon: Receipt },
  { to: "/backoffice-finance/recurrents", label: "Autres revenus / charges", icon: Repeat },
  { to: "/backoffice-finance/regles", label: "Règles catégorisation", icon: ListChecks },
  { to: "/backoffice-finance/simulations", label: "Simulations", icon: Wand2 },
];

export default function BackofficeLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Ferme le drawer sur navigation
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="backoffice-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-slate-800 bg-slate-950/85">
        <div className="max-w-[1600px] mx-auto px-3 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="bof-mobile-menu-btn"
              aria-label="Ouvrir le menu"
            >
              <Menu className="w-4 h-4" />
            </button>
            <Link to="/backoffice-finance" className="flex items-center gap-2 md:gap-3" data-testid="bof-logo">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 border border-amber-300/40 flex items-center justify-center shadow-lg shadow-amber-900/40">
                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-slate-950" strokeWidth={2.5} />
              </div>
              <div className="leading-tight hidden sm:block">
                <div className="font-black text-[10px] md:text-xs tracking-widest uppercase text-amber-400">Back-Office</div>
                <div className="font-black text-xs md:text-sm tracking-tight">Pilotage Foncière</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-1.5 md:gap-3">
            <span className="hidden md:inline text-xs text-slate-400" data-testid="bof-user-email">{user?.email}</span>
            <Link
              to="/admin"
              className="text-xs px-2 md:px-3 py-1.5 md:py-2 border border-slate-700 rounded hover:bg-slate-800 flex items-center gap-1.5"
              data-testid="bof-back-to-admin"
              title="Admin site"
            >
              <ExternalLink className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin site</span>
            </Link>
            <button
              onClick={logout}
              className="text-xs px-2 md:px-3 py-1.5 md:py-2 border border-slate-700 rounded hover:bg-slate-800 flex items-center gap-1.5"
              data-testid="bof-logout"
              title="Déconnexion"
            >
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto flex gap-0 relative">
        {/* Backdrop pour drawer mobile */}
        {menuOpen && (
          <button
            className="md:hidden fixed inset-0 bg-black/70 z-40"
            onClick={() => setMenuOpen(false)}
            aria-label="Fermer le menu"
            data-testid="bof-mobile-backdrop"
          />
        )}

        {/* Sidebar / Drawer mobile */}
        <aside
          className={`w-64 shrink-0 border-r border-slate-800 bg-slate-950 md:bg-transparent md:min-h-[calc(100vh-4rem)] py-4 md:py-6 px-3 z-50
                     md:relative md:translate-x-0 md:block
                     fixed inset-y-0 left-0 top-0 md:top-auto pt-4 md:pt-6
                     overflow-y-auto transition-transform duration-200
                     ${menuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
          data-testid="bof-sidebar-aside"
        >
          {/* Header du drawer mobile */}
          <div className="md:hidden flex items-center justify-between mb-4 px-2">
            <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Navigation</span>
            <button onClick={() => setMenuOpen(false)} className="text-slate-400 hover:text-white" data-testid="bof-mobile-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="space-y-1" data-testid="bof-sidebar">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-amber-400/10 text-amber-300 border-l-2 border-amber-400"
                      : "text-slate-300 hover:bg-slate-800/60 hover:text-white border-l-2 border-transparent"
                  }`
                }
                data-testid={`bof-nav-${item.to.split("/").pop() || "home"}`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 mx-1 p-3 border border-slate-800 rounded-md bg-slate-900/60">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Mode banque</div>
            <div className="text-xs text-slate-300">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-400/10 border border-amber-400/30 text-amber-300 rounded">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" /> Manuel (CSV)
              </span>
            </div>
            <div className="text-[10px] text-slate-500 mt-2 leading-snug">
              Automatique via Powens/Bridge à venir (souscription API requise).
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-3 md:gap-4 mb-5 md:mb-8">
            <div className="min-w-0">
              {title && (
                <h1 className="text-2xl md:text-3xl font-black tracking-tight text-white" data-testid="bof-page-title">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-xs md:text-sm text-slate-400 mt-1 md:mt-1.5 max-w-2xl">{subtitle}</p>
              )}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}

export const Card = ({ children, className = "", ...rest }) => (
  <div
    className={`bg-slate-900/60 border border-slate-800 rounded-lg p-4 md:p-5 ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const KpiCard = ({ label, value, sub, tone = "amber", testid }) => {
  const tones = {
    amber: "text-amber-300",
    green: "text-emerald-300",
    red: "text-red-300",
    slate: "text-slate-200",
    blue: "text-sky-300",
  };
  return (
    <Card data-testid={testid} className="min-w-[140px]">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl md:text-2xl font-black mt-1.5 md:mt-2 ${tones[tone] || tones.amber}`}>{value}</div>
      {sub && <div className="text-[10px] md:text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
};

export const fmtEUR = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
};

export const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
};

export const Btn = ({ children, variant = "primary", className = "", ...rest }) => {
  const v = {
    primary: "bg-amber-400 text-slate-950 hover:bg-amber-300 border-amber-500",
    ghost: "bg-transparent text-slate-300 hover:bg-slate-800 border-slate-700",
    danger: "bg-red-500/10 text-red-300 hover:bg-red-500/20 border-red-800",
  }[variant];
  return (
    <button
      className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded border transition-colors ${v} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};
